import { useEffect, useMemo, useRef, useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { useCombobox } from 'downshift'
import { get, isEmpty } from 'lodash'
import { matchSorter } from 'match-sorter'
import type { FieldValues } from 'react-hook-form'
import { useController, useFormContext } from 'react-hook-form'

import { useUnique } from '@subaio/components'
import { isNonNullable } from '@subaio/utils'

import type { CommonSelectProps } from '../CommonProps'
import {
	ButtonClear,
	ControlContainer,
	FieldWrapper,
	HintContainer,
	InputComponent,
	InputContainer,
	InputError,
	Label,
	MenuIndicator,
	OptionalHint,
	SelectMenu,
} from '../Shared'
import { InlineValue } from './InlineValue'
import { ValueWrapper } from './ValueWrapper'

export const CodeSelect = <TFieldValues extends FieldValues, TPath extends keyof TFieldValues & string>({
	showOptionalHint = false,
	isFullWidth = true,
	isRequired = false,
	optionsMaxVisible = 40,
	isReadOnly = false,
	isDisabled = false,
	name,
	label,
	hint,
	placeholder,
	options,
	shouldAutofocus = false,
	isLoading = false,
	isClearable = true,
	...rest
}: CommonSelectProps<TPath>) => {
	const id = useUnique(name)
	const {
		formState: { errors },
		control,
	} = useFormContext<TFieldValues>()
	const [inputValue, setInputValue] = useState<string>('')
	const filteredOptions = useMemo(
		() => (isEmpty(inputValue) ? options : matchSorter(options, inputValue, { keys: ['label'] })),
		[options, inputValue],
	)
	const { field } = useController<TFieldValues, any>({
		name,
		control,
		rules: { required: isRequired ? 'This field is required' : false },
	})
	const inputRef = useRef<HTMLInputElement>(null)

	const {
		isOpen: isMenuOpen,
		selectedItem,
		getLabelProps,
		getMenuProps,
		highlightedIndex,
		getItemProps,
		getInputProps,
		reset,
		openMenu: onOpenMenu,
		closeMenu: onCloseMenu,
	} = useCombobox({
		id,
		initialInputValue: options.find(({ value }) => value === field.value)?.label ?? '',
		initialSelectedItem: options.find(({ value }) => value === field.value),
		items: filteredOptions,
		itemToString: () => '',
		onSelectedItemChange: ({ selectedItem }) => field.onChange(selectedItem?.value),
		onIsOpenChange: ({ isOpen }) => {
			// If the user clears the field and exits the menu, we assume the user wants to reset the field
			if (!isOpen) setInputValue('')
		},
		onInputValueChange: ({ inputValue }) => setInputValue(inputValue ?? ''),
	})

	const { hoverProps, isHovered } = useHover({})
	const { isFocusVisible, focusProps, isFocused } = useFocusRing({
		isTextInput: true,
		autoFocus: shouldAutofocus,
	})

	const error = get(errors, name)
	const hasError = Boolean(error)

	useEffect(() => {
		// Makes sure the input is cleared if the form is reset outside of its own controller
		if (field.value === undefined) reset()
	}, [field.value, reset])

	return (
		<FieldWrapper isFullWidth={isFullWidth} minWidth={170} {...rest}>
			<Label {...getLabelProps()} htmlFor={id}>
				<span>
					{label} {showOptionalHint && !isRequired && <OptionalHint>(Optional)</OptionalHint>}
				</span>
			</Label>
			<InputContainer
				{...hoverProps}
				hasError={hasError}
				isReadOnly={isReadOnly}
				isDisabled={isDisabled}
				isHovered={isHovered}
				isFocusVisible={isFocusVisible}
				isFocus={isFocused}
				onClick={(event) => {
					if (event.defaultPrevented || isDisabled || isReadOnly) return undefined

					onOpenMenu()
					inputRef.current?.focus()
				}}
			>
				<ValueWrapper>
					<InputComponent
						{...(getInputProps({
							ref: inputRef,
							name,
							placeholder,
							onFocus: onOpenMenu,
							onClick: onOpenMenu,
						}) as Record<string, unknown>)}
						{...focusProps}
						value={inputValue}
						hasError={hasError}
						disabled={isDisabled || isReadOnly}
						id={id}
						autoFocus={shouldAutofocus}
						shouldShrink={!inputValue && Boolean(selectedItem)}
						onFocus={(event) => {
							event.target.select()
							onOpenMenu()
							if (focusProps.onFocus) focusProps.onFocus(event)
						}}
						onBlur={async (event) => {
							onCloseMenu()
							if (focusProps.onBlur) focusProps.onBlur(event)
						}}
					/>
					<InlineValue
						label={!inputValue && selectedItem ? selectedItem?.label : undefined}
						icon={!inputValue && selectedItem ? selectedItem?.icon : undefined}
					/>
				</ValueWrapper>
				<ControlContainer>
					{field.value !== undefined && isClearable && <ButtonClear onClick={reset} />}
					{!isDisabled && !isReadOnly && <MenuIndicator isMenuOpen={isMenuOpen} isLoading={isLoading} />}
				</ControlContainer>
			</InputContainer>
			<SelectMenu
				{...(getMenuProps({}, { suppressRefError: true }) as Record<string, unknown>)}
				getItemProps={getItemProps}
				highlightedOptionIndex={highlightedIndex}
				isOpen={isMenuOpen}
				optionsMaxVisible={optionsMaxVisible}
				options={filteredOptions}
				selectedItems={[selectedItem].filter(isNonNullable)}
			/>
			<HintContainer isVisible={Boolean(hint) && !hasError}>{hint}</HintContainer>
			<InputError hasError={hasError} errorMessage={error?.message as string | undefined} hasFocus={isFocusVisible} />
		</FieldWrapper>
	)
}
